/**
 * Visualizer Studio import parsing — turns an exported file into something the
 * ported Scene engine can render here.
 *
 * Two formats, both JSON with media embedded as base64 (the original media URLs
 * point at the EXPORTER's localhost backend and are dead here, so we decode the
 * base64 into Blob object-URLs and remap every reference):
 *   - `.vizproject` (format 'visualizer-project'): a full StudioProject timeline.
 *   - `.visualizer`  (format 'visualizer-scene'):   one or more standalone scene
 *     presets. We render the first as a single, always-active clip.
 */

import type { StudioProject, SceneClip } from './studio/types';
import type { ScenenPreset } from './scenes';

export interface VizProjectFile {
  format: 'visualizer-project';
  version: number;
  exportedAt: number;
  project: StudioProject;
  media: Record<string, EmbeddedMedia>;
}

export interface VisualizerSceneFile {
  format: 'visualizer-scene';
  version: number;
  exportedAt: number;
  scenes: ScenenPreset[];
  media: Record<string, EmbeddedMedia>;
}

interface EmbeddedMedia { kind: 'video' | 'image' | 'audio'; filename: string; mime: string; data: string }

export interface ParsedVizProject {
  project: StudioProject;
  /** Remapped (object-URL) audio source for the song, or null. */
  audioUrl: string | null;
  bpm: number;
  /** Revoke every object-URL created for this import. Call on unload. */
  dispose: () => void;
}

/** Decode a base64 string (no data: prefix) into a Blob object-URL. */
function base64ToObjectUrl(b64: string, mime: string): string {
  const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
  return URL.createObjectURL(new Blob([bytes], { type: mime }));
}

/** Decode every embedded media file → object URL, keyed by its ORIGINAL url. */
function decodeMedia(media: Record<string, EmbeddedMedia> | undefined): { urlMap: Record<string, string>; created: string[] } {
  const urlMap: Record<string, string> = {};
  const created: string[] = [];
  for (const [oldUrl, m] of Object.entries(media ?? {})) {
    try {
      const objUrl = base64ToObjectUrl(m.data, m.mime || 'application/octet-stream');
      urlMap[oldUrl] = objUrl;
      created.push(objUrl);
    } catch {
      /* skip an entry that fails to decode rather than aborting the load */
    }
  }
  return { urlMap, created };
}

/** Remap a scene preset's media URLs through the decoded object-URL map. */
function remapScene(scene: any, urlMap: Record<string, string>): void {
  const remap = (url: string | null | undefined) => (url ? (urlMap[url] ?? url) : null);
  if (!scene) return;
  scene.backgroundVideoUrl = remap(scene.backgroundVideoUrl);
  scene.backgroundImageUrl = remap(scene.backgroundImageUrl);
  scene.textureUrl = remap(scene.textureUrl);
}

/** Wrap a single scene preset into a one-clip StudioProject (always active). */
function sceneToProject(scene: ScenenPreset, name: string): StudioProject {
  const clip: SceneClip = {
    id: 'single-0', kind: 'scene', startTime: 0, duration: 1e9,
    scene, label: scene.name ?? 'Scene', enabled: true, locked: false,
  };
  return {
    id: 'single', name, createdAt: 0, updatedAt: 0,
    audioUrl: null, audioName: '', duration: 1e9, bpm: 120, bpmOffset: 0,
    timeline: {
      sceneTrack: { id: 'st', kind: 'scene', clips: [clip] },
      transitionTrack: { id: 'tt', kind: 'transition', clips: [] },
      lyricTrack: { id: 'lt', kind: 'lyric', clips: [] },
    },
  };
}

/** Parse + rehost a `.vizproject` (full timeline). null if not that format. */
export function parseVizProject(rawJson: string | object): ParsedVizProject | null {
  const raw: any = typeof rawJson === 'string' ? JSON.parse(rawJson) : rawJson;
  if (!(raw && raw.format === 'visualizer-project' && raw.project?.timeline?.sceneTrack?.clips)) return null;

  const { urlMap, created } = decodeMedia(raw.media);
  const project: StudioProject = JSON.parse(JSON.stringify(raw.project));
  project.audioUrl = project.audioUrl ? (urlMap[project.audioUrl] ?? project.audioUrl) : null;
  for (const clip of project.timeline.sceneTrack.clips) remapScene(clip.scene, urlMap);

  return {
    project, audioUrl: project.audioUrl, bpm: project.bpm || 120,
    dispose: () => { created.forEach(u => { try { URL.revokeObjectURL(u); } catch { /* noop */ } }); },
  };
}

/** Parse + rehost a `.visualizer` (single scene). null if not that format. */
export function parseVisualizerScene(rawJson: string | object): ParsedVizProject | null {
  const raw: any = typeof rawJson === 'string' ? JSON.parse(rawJson) : rawJson;
  if (!(raw && raw.format === 'visualizer-scene' && Array.isArray(raw.scenes) && raw.scenes.length > 0)) return null;

  const { urlMap, created } = decodeMedia(raw.media);
  const scene: ScenenPreset = JSON.parse(JSON.stringify(raw.scenes[0]));
  remapScene(scene, urlMap);
  const project = sceneToProject(scene, scene.name ?? 'Visualizer');

  return {
    project, audioUrl: null, bpm: 120,
    dispose: () => { created.forEach(u => { try { URL.revokeObjectURL(u); } catch { /* noop */ } }); },
  };
}

/** Dispatch on format: parse either a `.vizproject` or a `.visualizer`. */
export function parseVizFile(rawJson: string | object): ParsedVizProject | null {
  const raw: any = typeof rawJson === 'string' ? JSON.parse(rawJson) : rawJson;
  if (raw?.format === 'visualizer-project') return parseVizProject(raw);
  if (raw?.format === 'visualizer-scene') return parseVisualizerScene(raw);
  return null;
}
